import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Archive, RotateCcw, Search, AlertTriangle, Loader2 } from 'lucide-react';

export default function TestDataCleanup() {
    const { t } = useTranslation();
    const [orgs, setOrgs] = useState([]);
    const [locations, setLocations] = useState([]);
    const [orgId, setOrgId] = useState('');
    const [locId, setLocId] = useState('all');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [emailContains, setEmailContains] = useState('');
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState(false);
    const [confirming, setConfirming] = useState(false);

    useEffect(() => {
        api.get('/organizations').then(r => setOrgs(r.data || [])).catch(() => {});
    }, []);
    useEffect(() => {
        setLocId('all');
        if (!orgId) { setLocations([]); return; }
        api.get(`/locations?organization_id=${orgId}`).then(r => setLocations(r.data || [])).catch(() => setLocations([]));
    }, [orgId]);

    const scope = () => ({
        organization_id: orgId,
        location_id: locId === 'all' ? null : locId,
        date_from: dateFrom,
        date_to: dateTo,
        email_contains: emailContains.trim() || null,
    });
    const ready = orgId && dateFrom && dateTo;

    const doPreview = async () => {
        setBusy(true); setConfirming(false);
        try {
            const r = await api.post('/admin/test-data/preview', scope());
            setPreview(r.data);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Preview failed');
        } finally { setBusy(false); }
    };

    const doArchive = async () => {
        setBusy(true);
        try {
            const r = await api.post('/admin/test-data/archive', scope());
            const a = r.data.archived;
            toast.success(t('testData.archivedToast', { defaultValue: 'Archived: {{rs}} Review Shield records, {{sess}} sessions, {{taps}} guest tap events', rs: a.review_shield_records, sess: a.hotel_sessions, taps: a.guest_tap_events }));
            setConfirming(false);
            await doPreview();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Archive failed');
        } finally { setBusy(false); }
    };

    const doRestore = async () => {
        setBusy(true);
        try {
            const r = await api.post('/admin/test-data/restore', scope());
            const a = r.data.restored;
            toast.success(t('testData.restoredToast', { defaultValue: 'Restored: {{rs}} records, {{sess}} sessions, {{taps}} tap events', rs: a.review_shield_records, sess: a.hotel_sessions, taps: a.guest_tap_events }));
            await doPreview();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Restore failed');
        } finally { setBusy(false); }
    };

    const aff = preview?.affected;

    return (
        <div className="p-4 sm:p-6 max-w-3xl space-y-6" data-testid="test-data-cleanup-page">
            <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {t('testData.title', 'Archive / Clear Test Data')}
                </h1>
                <p className="text-muted-foreground mt-1 text-sm">
                    {t('testData.subtitle', 'Hide test activity from operational Review Shield & Hotel views. Nothing is deleted — archived records remain available to Executive Summary and historical reporting, and can be restored.')}
                </p>
            </div>

            <Card>
                <CardHeader><CardTitle className="text-lg">{t('testData.scope', 'Scope')}</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                            <Label>{t('common.organization', 'Organization')}</Label>
                            <Select value={orgId} onValueChange={setOrgId}>
                                <SelectTrigger data-testid="td-org-select"><SelectValue placeholder={t('testData.selectOrg', 'Select organization')} /></SelectTrigger>
                                <SelectContent>
                                    {orgs.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5">
                            <Label>{t('common.location', 'Location')}</Label>
                            <Select value={locId} onValueChange={setLocId} disabled={!orgId}>
                                <SelectTrigger data-testid="td-loc-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">{t('testData.allLocations', 'All locations')}</SelectItem>
                                    {locations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5">
                            <Label>{t('testData.dateFrom', 'Date from')}</Label>
                            <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} data-testid="td-date-from" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>{t('testData.dateTo', 'Date to (inclusive)')}</Label>
                            <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} data-testid="td-date-to" />
                        </div>
                        <div className="space-y-1.5 sm:col-span-2">
                            <Label>{t('testData.emailContains', 'Guest email contains (optional)')}</Label>
                            <Input value={emailContains} onChange={e => setEmailContains(e.target.value)}
                                placeholder="e.g. mytest@ or +test" data-testid="td-email-contains" />
                        </div>
                    </div>
                    <Button onClick={doPreview} disabled={!ready || busy} data-testid="td-preview-btn">
                        {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                        {t('testData.preview', 'Preview affected records')}
                    </Button>
                </CardContent>
            </Card>

            {preview && (
                <Card data-testid="td-preview-card">
                    <CardHeader><CardTitle className="text-lg">{t('testData.previewTitle', 'Preview')}</CardTitle></CardHeader>
                    <CardContent className="space-y-4">
                        <div className="text-sm text-muted-foreground">
                            {preview.organization} · {preview.location} · {preview.date_from} → {preview.date_to} ({t('testData.inclusive', 'inclusive')}, {preview.timezone})
                            {preview.email_contains && <> · "{preview.email_contains}"</>}
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                            {[
                                [t('testData.rsRecords', 'Review Shield records'), aff.review_shield_records, 'td-count-rs'],
                                [t('testData.hotelJourneys', 'Hotel guest journeys'), aff.hotel_guest_journeys, 'td-count-hotel'],
                                [t('testData.sessions', 'Guest sessions'), aff.hotel_sessions, 'td-count-sessions'],
                                [t('testData.tapEvents', 'Guest tap events'), aff.guest_tap_events, 'td-count-taps'],
                            ].map(([label, val, tid]) => (
                                <div key={tid} className="rounded-lg border p-3 text-center">
                                    <div className="text-2xl font-bold" data-testid={tid}>{val}</div>
                                    <div className="text-xs text-muted-foreground mt-1">{label}</div>
                                </div>
                            ))}
                        </div>
                        {(preview.already_archived?.review_shield_records > 0 || preview.already_archived?.hotel_sessions > 0) && (
                            <div className="text-xs text-muted-foreground">
                                {t('testData.alreadyArchived', 'Already archived in this scope')}: {preview.already_archived.review_shield_records} {t('testData.records', 'records')}, {preview.already_archived.hotel_sessions} sessions
                                <Button variant="ghost" size="sm" className="ml-2 h-7" onClick={doRestore} disabled={busy} data-testid="td-restore-btn">
                                    <RotateCcw className="w-3.5 h-3.5 mr-1" />{t('testData.restore', 'Restore')}
                                </Button>
                            </div>
                        )}
                        {!confirming ? (
                            <Button variant="destructive" onClick={() => setConfirming(true)}
                                disabled={busy || (aff.review_shield_records === 0 && aff.hotel_sessions === 0 && aff.guest_tap_events === 0)}
                                data-testid="td-archive-btn">
                                <Archive className="w-4 h-4 mr-2" />{t('testData.archive', 'Archive these records')}
                            </Button>
                        ) : (
                            <div className="rounded-lg border border-red-500/40 bg-red-500/5 p-4 space-y-3" data-testid="td-confirm-box">
                                <div className="flex items-start gap-2 text-sm">
                                    <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
                                    <span>{t('testData.confirmText', { defaultValue: 'Archive {{n}} Review Shield records ({{h}} hotel journeys, {{s}} sessions, {{tp}} tap events)? They will be hidden from operational views but kept for Executive Summary / history and can be restored.', n: aff.review_shield_records, h: aff.hotel_guest_journeys, s: aff.hotel_sessions, tp: aff.guest_tap_events })}</span>
                                </div>
                                <div className="flex gap-2">
                                    <Button variant="destructive" onClick={doArchive} disabled={busy} data-testid="td-confirm-archive-btn">
                                        {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Archive className="w-4 h-4 mr-2" />}
                                        {t('testData.confirmArchive', 'Yes, archive')}
                                    </Button>
                                    <Button variant="outline" onClick={() => setConfirming(false)} disabled={busy} data-testid="td-cancel-btn">
                                        {t('common.cancel', 'Cancel')}
                                    </Button>
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>
            )}
        </div>
    );
}
